import type { GroupSettingsUpdateRequestData } from '@/types/requests/groupSettingsUpdateRequestData.type';
import type { UpdateUserGroupsRequestData } from '@/types/requests/updateUserGroupsRequestData.type';
import apiService from './api';
import type {
  BulletinGroupSettingsUpdateRequestData,
  CreateGroupRequestData,
  CurrentImportMembersResponse,
  Group,
  GroupAdminMemberLookup,
  GroupListRequestFiltersData,
  GroupLookup,
  GroupLookupListingItem,
  GroupSettingsData,
  GroupShareOptions,
  ImportMembersResponse,
  JoinUserToGroupParams,
  PagedListRequest,
  PagedListResponse,
  UserJoinedGroupView,
} from '@/types';
import type { SortChanges } from '@/types/sort.type';
import { GROUPS_LIST_SORT_FIELD } from '@/types/requests/groupsListRequestFiltersData.type';
import type { MassUpdateDeliveryMethodsRequestData } from '@/types/requests/massUpdateDeliveryMethodsRequestData.type';
import type { MassUpdateDeliveryMethodsResponse } from '@/types/responses/massUpdateDeliveryMethodsResponse.type';
import { downloadFile, isRequestCancelledError } from '@/helpers';

type GetCurrentMembersImportParams = {
  churchId: string;
  groupId: string;
  isInitialPolling: boolean;
  signal?: AbortSignal;
};

class GroupsService {
  getGroupsList = async (churchId: string, filters?: GroupListRequestFiltersData) => {
    try {
      const response = await apiService.get<PagedListResponse<GroupLookup>>(`/admin/church/${churchId}/groups`, {
        params: filters,
      });
      return response;
    } catch (e) {
      console.error('Error fetching groups:', e);
      throw e;
    }
  };

  createGroup = async (churchId: string, data: CreateGroupRequestData) => {
    try {
      const response = await apiService.post<Group>(`/admin/church/${churchId}/groups`, data);
      return response.data;
    } catch (e) {
      console.error('Error creating group:', e);
      throw e;
    }
  };

  checkKeywordAvailability = async (churchId: string, keyword: string, groupId?: string, signal?: AbortSignal) => {
    try {
      const params: Record<string, string> = { keyword };
      if (groupId) {
        params.groupId = groupId;
      }
      const response = await apiService.get<{ isAvailable: boolean; message: string }>(
        `/admin/church/${churchId}/groups/keyword/check`,
        { params, signal }
      );
      return response.data;
    } catch (e) {
      console.error('Error checking keyword availability:', e);
      throw e;
    }
  };

  getUserJoinedGroups = async (churchId: string) => {
    try {
      const response = await apiService.get<UserJoinedGroupView[]>(`/admin/church/${churchId}/groups/user-joined`);
      return response.data;
    } catch (e) {
      console.error('Error fetching user joined groups:', e);
      throw e;
    }
  };

  updateUserJoinedGroups = async (churchId: string, data: UpdateUserGroupsRequestData) => {
    try {
      const response = await apiService.put<UserJoinedGroupView[]>(
        `/admin/church/${churchId}/groups/user-joined`,
        data
      );
      return response.data;
    } catch (e) {
      console.error('Error updating user joined groups:', e);
      throw e;
    }
  };

  joinUserToGroup = async ({ groupId, deliveryMethods }: JoinUserToGroupParams) => {
    try {
      const response = await apiService.put<UserJoinedGroupView>(`/groups/${groupId}`, { deliveryMethods });
      return response.data;
    } catch (e) {
      console.error('Error joining user to group:', e);
      throw e;
    }
  };

  getGroupSettings = async (churchId: string, groupId: string) => {
    try {
      const response = await apiService.get<GroupSettingsData>(`/admin/church/${churchId}/groups/${groupId}/settings`);
      return response.data;
    } catch (e) {
      console.error('Error fetching group settings:', e);
      throw e;
    }
  };

  updateGroupSettings = async (churchId: string, groupId: string, data: GroupSettingsUpdateRequestData) => {
    try {
      const response = await apiService.put<Group>(`/admin/church/${churchId}/groups/${groupId}/settings`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating group settings:', e);
      throw e;
    }
  };

  updateBulletinGroupSettings = async (
    churchId: string,
    groupId: string,
    data: BulletinGroupSettingsUpdateRequestData
  ) => {
    try {
      const response = await apiService.put<GroupSettingsData>(
        `/admin/church/${churchId}/groups/${groupId}/bulletin-settings`,
        data
      );
      return response.data;
    } catch (e) {
      console.error('Error updating bulletin group settings:', e);
      throw e;
    }
  };

  toggleGroupPinned = async (churchId: string, groupId: string, isPinned: boolean) => {
    try {
      const response = await apiService.patch<GroupLookup>(`/admin/church/${churchId}/groups/${groupId}/pin`, {
        isPinned,
      });
      return response.data;
    } catch (e) {
      console.error('Error toggling group pinned:', e);
      throw e;
    }
  };

  getGroupShareOptions = async (churchId: string, groupId: string) => {
    try {
      const response = await apiService.get<GroupShareOptions>(
        `/admin/church/${churchId}/groups/${groupId}/shared-options`
      );
      return response.data;
    } catch (e) {
      console.error('Error fetching group share options:', e);
      throw e;
    }
  };

  updateGroupShareOptions = async (churchId: string, groupId: string, data: GroupShareOptions) => {
    try {
      const response = await apiService.put<GroupShareOptions>(
        `/admin/church/${churchId}/groups/${groupId}/shared-options`,
        data
      );
      return response.data;
    } catch (e) {
      console.error('Error updating group share options:', e);
      throw e;
    }
  };

  getGroupAdmins = async (churchId: string, groupId: string) => {
    try {
      const response = await apiService.get<GroupAdminMemberLookup[]>(
        `/admin/church/${churchId}/groups/${groupId}/admins`
      );
      return response.data;
    } catch (e) {
      console.error('Error fetching group admins:', e);
      throw e;
    }
  };

  updateGroupAdmins = async (churchId: string, groupId: string, data: string[]) => {
    try {
      const response = await apiService.put<void>(`/admin/church/${churchId}/groups/${groupId}/admins`, {
        admins: data,
      });
      return response.data;
    } catch (e) {
      console.error('Error updating group admins:', e);
      throw e;
    }
  };

  deleteGroup = async (churchId: string, groupId: string) => {
    try {
      const response = await apiService.delete<void>(`/admin/church/${churchId}/groups/${groupId}`);
      return response.data;
    } catch (e) {
      console.error('Error deleting group:', e);
      throw e;
    }
  };

  deleteAllMembers = async (churchId: string, groupId: string) => {
    try {
      const response = await apiService.delete<void>(`/admin/church/${churchId}/groups/${groupId}/members`);
      return response.data;
    } catch (e) {
      console.error('Error deleting all members:', e);
      throw e;
    }
  };

  addMembersByIds = async (churchId: string, groupId: string, memberIds: string[]) => {
    try {
      const response = await apiService.put<void>(`/admin/church/${churchId}/groups/${groupId}/members`, memberIds);
      return response.data;
    } catch (e) {
      console.error('Error adding members by ids:', e);
      throw e;
    }
  };

  massUpdateDeliveryMethods = async (churchId: string, groupId: string, data: MassUpdateDeliveryMethodsRequestData) => {
    try {
      const response = await apiService.post<MassUpdateDeliveryMethodsResponse>(
        `/admin/church/${churchId}/groups/${groupId}/members/delivery-methods/mass-update`,
        data
      );
      return response.data;
    } catch (e) {
      console.error('Error mass updating delivery methods:', e);
      throw e;
    }
  };

  getMembersImportSample = async (churchId: string, groupId: string) => {
    try {
      const response = await apiService.get<Blob>(`/admin/church/${churchId}/groups/${groupId}/members/import/sample`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (e) {
      console.error('Error fetching members import sample:', e);
      throw e;
    }
  };

  importMembers = async (churchId: string, groupId: string, csvFile: File) => {
    try {
      const formData = new FormData();
      formData.append('csv', csvFile);

      const response = await apiService.post<ImportMembersResponse>(
        `/admin/church/${churchId}/groups/${groupId}/members/import`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (e) {
      console.error('Error importing members:', e);
      throw e;
    }
  };

  getCurrentMembersImport = async ({ churchId, groupId, isInitialPolling, signal }: GetCurrentMembersImportParams) => {
    try {
      const response = await apiService.get<CurrentImportMembersResponse>(
        `/admin/church/${churchId}/groups/${groupId}/members/import/current`,
        {
          params: { isInitialPolling },
          signal,
        }
      );
      return response.data;
    } catch (e) {
      if (!isRequestCancelledError(e)) {
        console.error('Error fetching current members import:', e);
      }
      throw e;
    }
  };

  lookupGroups = async (
    churchId: string,
    params: PagedListRequest & {
      term?: string;
      parentCandidates?: boolean;
      hideEmptyStaffGroups?: boolean;
    } & Partial<SortChanges<GROUPS_LIST_SORT_FIELD>>
  ) => {
    try {
      const response = await apiService.get<PagedListResponse<GroupLookupListingItem>>(
        `/admin/church/${churchId}/groups/lookup`,
        {
          params,
        }
      );
      return response;
    } catch (e) {
      console.error('Error looking up groups:', e);
      throw e;
    }
  };

  exportMembers = async (churchId: string, groupId: string) => {
    try {
      downloadFile(`/admin/church/${churchId}/groups/${groupId}/members/export`, `members-${groupId}.csv`);
    } catch (e) {
      console.error('Error exporting members:', e);
      throw e;
    }
  };

  exportUnsubscribedMembers = async (churchId: string, groupId: string) => {
    try {
      downloadFile(
        `/admin/church/${churchId}/groups/${groupId}/members/export/unsubscribed`,
        `unsubscribed-members-${groupId}.csv`
      );
    } catch (e) {
      console.error('Error exporting unsubscribed members:', e);
      throw e;
    }
  };
}

export default new GroupsService();
